import { Component } from "react"
import PropTypes from "prop-types"
import moment from "moment"

export default class LineComparisonChart extends Component {
  getSettings(props, Highcharts) {
    const { timePeriodInDays, data, metric } = props

    return {
      title: {
        text: "",
      },
      subtitle: {
        text: "",
      },
      chart: {
        animation: false,
        renderTo: this.chartContainer,
        height: props.height,
        spacing: [10, 0, 10, 0],
        marginTop: 40,
      },
      credits: { enabled: false },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          animation: false,
        },
      },
      xAxis: {
        type: "datetime",
        tickmarkPlacement: "on",
        labels: {
          step: Math.round(timePeriodInDays / 4),
          style: {
            color: "#999",
            fontSize: "14",
            fontWeight: "700",
          },
        },
        lineWidth: 0,
        tickWidth: 0,
        categories: this.xAxisCategories(data).slice(-timePeriodInDays),
      },
      yAxis: [
        {
          opposite: true,
          title: {
            enabled: false,
          },
        },
        {
          title: {
            enabled: false,
          },
          labels: {
            style: {
              color: "#999",
              fontSize: "14",
              fontWeight: "700",
            },
            formatter: data => {
              return Highcharts.numberFormat(data.value, 0)
            },
          },
          minRange: 10,
          offset: 10,
          gridLineWidth: 4,
          gridLineColor: "#f5f5f5",
          floor: Math.min(
            ...this.slicedDataForTimePeriod(
              timePeriodInDays,
              data,
              metric
            ).total.map(d => d.y)
          ),
        },
      ],
      series: [
        {
          name: `Last ${timePeriodInDays} days`,
          yAxis: 1,
          color: props.lineColorA,
          lineWidth: 3,
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
          type: props.lineOnly ? "line" : "area",
          showInLegend: false,
          fillOpacity: 0.1,
          data: this.slicedDataForTimePeriod(timePeriodInDays, data, metric)
            .current,
        },
        {
          name: `Prior ${timePeriodInDays} days`,
          yAxis: 1,
          color: props.lineColorB,
          lineWidth: 3,
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
          type: "line",
          showInLegend: false,
          data: this.slicedDataForTimePeriod(timePeriodInDays, data, metric)
            .previous,
        },
      ],
      tooltip: {
        animation: false,
        backgroundColor: "#333",
        borderWidth: 0,
        borderRadius: 10,
        headerFormat: '<span style="color: #999">{point.key}</span>',
        padding: 8,
        pointFormat: `<br><b>{point.y} ${props.units}</b>`,
        shadow: false,
        style: {
          color: "#fff",
          fontSize: 14,
        },
      },
    }
  }

  async componentDidMount() {
    const Highcharts = await import("highcharts")
    Highcharts.setOptions(window.globalHighchartsOptions)

    this.chart = new Highcharts.Chart(this.getSettings(this.props, Highcharts))
  }

  componentWillUnmount() {
    this.chart.destroy()
  }

  xAxisCategories(data) {
    return data.map(row => moment(row.metric_date).format("MMM D"))
  }

  extractDataForTotalRunsLayer(data, metric) {
    return data.map(d => {
      return {
        y: parseInt(d[metric]),
        name: moment(d.metric_date).format("MMM D"),
      }
    })
  }

  slicedDataForTimePeriod(timePeriodInDays, data, metric) {
    return {
      current: this.extractDataForTotalRunsLayer(data, metric).slice(
        -timePeriodInDays
      ),
      previous: this.extractDataForTotalRunsLayer(data, metric).slice(
        -(timePeriodInDays * 2),
        -timePeriodInDays
      ),
      total: this.extractDataForTotalRunsLayer(data, metric).slice(
        -(timePeriodInDays * 2)
      ),
    }
  }

  async shouldComponentUpdate(nextProps) {
    if (this.props.data !== nextProps.data) {
      const Highcharts = await import("highcharts")
      Highcharts.setOptions(window.globalHighchartsOptions)

      this.chart.destroy()
      this.chart = new Highcharts.Chart(this.getSettings(nextProps, Highcharts))
    }
    return false
  }

  render() {
    return (
      <div
        className="chart-container round-grid-chart"
        ref={c => (this.chartContainer = c)}
      />
    )
  }
}

LineComparisonChart.propTypes = {
  data: PropTypes.array.isRequired,
  settings: PropTypes.object,
  timePeriodInDays: PropTypes.number.isRequired,
  units: PropTypes.string.isRequired,
  metric: PropTypes.string,
  height: PropTypes.number,
  lineColorA: PropTypes.string,
  lineColorB: PropTypes.string,
  lineOnly: PropTypes.bool,
}

LineComparisonChart.defaultProps = {
  timePeriodInDays: 7,
  lineColorA: "#7cb5ec",
  lineColorB: "#BBB",
  lineOnly: false,
  height: 300,
}
